// Utility functions for filtering meetings, agenda items, and speeches

export const extractUniqueHashtags = (meetings, agendaItems) => {
  const hashtagSet = new Set();
  
  // Only extract hashtags from agenda items (not from meetings)
  agendaItems.forEach(item => {
    if (item.hashtags && Array.isArray(item.hashtags)) {
      item.hashtags.forEach(hashtag => hashtagSet.add(hashtag));
    }
  });
  
  return Array.from(hashtagSet)
    .sort()
    .map(hashtag => ({ value: hashtag, label: hashtag }));
};

export const extractUniqueSpeakers = (speeches) => {
  const speakerSet = new Set();
  
  speeches.forEach(speech => {
    if (speech.speaker_first_name && speech.speaker_last_name) {
      const fullName = `${speech.speaker_first_name} ${speech.speaker_last_name}`;
      speakerSet.add(fullName);
    }
  });
  
  return Array.from(speakerSet)
    .sort()
    .map(speaker => ({ value: speaker, label: speaker }));
};

export const filterMeetingsByDate = (meetings, dateRange) => {
  if (!dateRange.start && !dateRange.end) {
    return meetings;
  }
  
  return meetings.filter(meeting => {
    const meetingDate = new Date(meeting.date_of_sitting);
    const startDate = dateRange.start ? new Date(dateRange.start) : null;
    const endDate = dateRange.end ? new Date(dateRange.end) : null;
    
    if (startDate && meetingDate < startDate) {
      return false;
    }
    
    if (endDate && meetingDate > endDate) {
      return false;
    }
    
    return true;
  });
};

export const filterMeetingsByHashtags = (meetings, selectedHashtags, allAgendaItems) => {
  if (selectedHashtags.length === 0) {
    return meetings;
  }
  
  return meetings.filter(meeting => {
    // Only check if any agenda items in this meeting have the selected hashtags
    const hasAgendaItemWithHashtag = allAgendaItems.some(item => {
      if (item.meeting_id !== meeting.id) return false;
      if (!item.hashtags || !Array.isArray(item.hashtags)) return false;
      return selectedHashtags.some(hashtag => item.hashtags.includes(hashtag));
    });
    
    return hasAgendaItemWithHashtag;
  });
};

export const filterMeetingsBySpeakers = (meetings, selectedSpeakers, allAgendaItems, allSpeeches) => {
  if (selectedSpeakers.length === 0) {
    return meetings;
  }
  
  // Find agenda items that have speeches from selected speakers
  const agendaItemsWithSelectedSpeakers = new Set();
  
  allSpeeches.forEach(speech => {
    const speakerName = `${speech.speaker_first_name} ${speech.speaker_last_name}`;
    if (selectedSpeakers.includes(speakerName)) {
      agendaItemsWithSelectedSpeakers.add(speech.agenda_item_id);
    }
  });
  
  // Find meetings that contain these agenda items
  const meetingsWithSelectedSpeakers = new Set();
  
  allAgendaItems.forEach(item => {
    if (agendaItemsWithSelectedSpeakers.has(item.id)) {
      meetingsWithSelectedSpeakers.add(item.meeting_id);
    }
  });
  
  return meetings.filter(meeting => meetingsWithSelectedSpeakers.has(meeting.id));
};

export const filterAgendaItemsByHashtags = (agendaItems, selectedHashtags) => {
  if (selectedHashtags.length === 0) {
    return agendaItems;
  }
  
  return agendaItems.filter(item => {
    if (!item.hashtags || !Array.isArray(item.hashtags)) {
      return false;
    }
    
    return selectedHashtags.some(hashtag => 
      item.hashtags.includes(hashtag)
    );
  });
};

export const filterAgendaItemsBySpeakers = (agendaItems, selectedSpeakers, allSpeeches) => {
  if (selectedSpeakers.length === 0) {
    return agendaItems;
  }
  
  // Create a map of agenda item IDs that have speeches from selected speakers
  const agendaItemsWithSelectedSpeakers = new Set();
  
  allSpeeches.forEach(speech => {
    const speakerName = `${speech.speaker_first_name} ${speech.speaker_last_name}`;
    if (selectedSpeakers.includes(speakerName)) {
      agendaItemsWithSelectedSpeakers.add(speech.agenda_item_id);
    }
  });
  
  return agendaItems.filter(item => 
    agendaItemsWithSelectedSpeakers.has(item.id)
  );
};

export const applyAllFilters = (meetings, agendaItems, allSpeeches, filters) => {
  // Filter meetings by date first
  let filteredMeetings = filterMeetingsByDate(meetings, filters.dateRange);
  
  // Filter meetings by hashtags (check only agenda item hashtags)
  if (filters.selectedHashtags.length > 0) {
    filteredMeetings = filterMeetingsByHashtags(filteredMeetings, filters.selectedHashtags, agendaItems);
  }
  
  // Filter meetings by speakers (if agenda items in those meetings have speeches from selected speakers)
  if (filters.selectedSpeakers.length > 0) {
    filteredMeetings = filterMeetingsBySpeakers(filteredMeetings, filters.selectedSpeakers, agendaItems, allSpeeches);
  }
  
  // Get meeting IDs for agenda item filtering
  const filteredMeetingIds = new Set(filteredMeetings.map(m => m.id));
  
  // Filter agenda items by meeting membership
  let filteredAgendaItems = agendaItems.filter(item => 
    filteredMeetingIds.has(item.meeting_id)
  );
  
  // Apply hashtag filter to agenda items
  if (filters.selectedHashtags.length > 0) {
    filteredAgendaItems = filterAgendaItemsByHashtags(filteredAgendaItems, filters.selectedHashtags);
  }
  
  // Apply speaker filter to agenda items
  if (filters.selectedSpeakers.length > 0) {
    filteredAgendaItems = filterAgendaItemsBySpeakers(
      filteredAgendaItems, 
      filters.selectedSpeakers, 
      allSpeeches
    );
  }
  
  // Filter speeches based on filtered agenda items
  const filteredAgendaItemIds = new Set(filteredAgendaItems.map(item => item.id));
  const filteredSpeeches = allSpeeches.filter(speech => 
    filteredAgendaItemIds.has(speech.agenda_item_id)
  );
  
  return {
    meetings: filteredMeetings,
    agendaItems: filteredAgendaItems,
    speeches: filteredSpeeches
  };
};

export const getResultCounts = (filteredData) => {
  return {
    meetings: filteredData.meetings.length,
    agendaItems: filteredData.agendaItems.length,
    speeches: filteredData.speeches.length
  };
};

// Parliament Members Filter Functions

export const filterParliamentMembersBySearch = (members, searchText) => {
  if (!searchText) {
    return members;
  }
  
  const searchLower = searchText.toLowerCase();
  return members.filter(member => {
    const fullName = `${member.first_name} ${member.last_name}`.toLowerCase();
    return (
      fullName.includes(searchLower) ||
      (member.party && member.party.toLowerCase().includes(searchLower))
    );
  });
};

export const filterParliamentMembersByParties = (members, selectedParties) => {
  if (selectedParties.length === 0) {
    return members;
  }
  
  return members.filter(member => 
    member.party && selectedParties.includes(member.party)
  );
};


export const filterParliamentMembersByDateRange = (members, dateRange, memberSpeechData) => {
  if (!dateRange.start && !dateRange.end) {
    return members;
  }
  
  return members.filter(member => {
    const speechData = memberSpeechData[member.id];
    if (!speechData || !speechData.lastActive) {
      return false; // No speech activity
    }
    
    const lastActiveDate = new Date(speechData.lastActive);
    const startDate = dateRange.start ? new Date(dateRange.start) : null;
    const endDate = dateRange.end ? new Date(dateRange.end) : null;
    
    if (startDate && lastActiveDate < startDate) {
      return false;
    }
    
    if (endDate && lastActiveDate > endDate) {
      return false;
    }
    
    return true;
  });
};

export const filterParliamentMembersByHashtags = (members, selectedHashtags, memberSpeechData) => {
  if (selectedHashtags.length === 0) {
    return members;
  }
  
  return members.filter(member => {
    const speechData = memberSpeechData[member.id];
    if (!speechData || !speechData.topHashtags) {
      return false;
    }
    
    return selectedHashtags.some(hashtag => 
      speechData.topHashtags.includes(hashtag)
    );
  });
};

export const applyParliamentMemberFilters = (members, allSpeeches, allAgendaItems, filters, memberSpeechData) => {
  let filteredMembers = [...members];
  
  // Apply search filter
  if (filters.searchText) {
    filteredMembers = filterParliamentMembersBySearch(filteredMembers, filters.searchText);
  }
  
  // Apply party filter
  if (filters.selectedParties && filters.selectedParties.length > 0) {
    filteredMembers = filterParliamentMembersByParties(filteredMembers, filters.selectedParties);
  }
  
  
  // Apply date range filter
  if (filters.dateRange && (filters.dateRange.start || filters.dateRange.end)) {
    filteredMembers = filterParliamentMembersByDateRange(filteredMembers, filters.dateRange, memberSpeechData);
  }
  
  // Apply hashtag filter
  if (filters.selectedHashtags && filters.selectedHashtags.length > 0) {
    filteredMembers = filterParliamentMembersByHashtags(filteredMembers, filters.selectedHashtags, memberSpeechData);
  }
  
  return filteredMembers;
};

export const getParliamentMemberResultCounts = (filteredMembers) => {
  return {
    members: filteredMembers.length
  };
};
